<template>
  <VariantLink
    render-as="tr"
    :list="config.public.gtm.PRODUCT_VARIANTS_LIST"
    :variant="variant"
    :class="'group contents variant-table-row'"
    data-test="variant-table-row"
    @click.prevent="trackNavigation()"
  >
    <!-- Fixed area (left). Art.nr, image etc. -->
    <td
      data-test="variant-table-header-fixed-left"
      class="@4xl:sticky left-0 flex h-14 @4xl:h-[46px] variant-table-cell z-[2] hover:z-10"
      :class="{
        selected: isSelected,
        last: !variantAttributes.length && !isLoggedIn,
        '@4xl:shadow-[5px_5px_8px_0_rgba(0,0,0,0.08)] ': isScrolled,
        '@4xl:border-b-gray-200': isScrolled && !isSelected,
      }"
      @click.prevent.stop
    >
      <CImage
        class="cursor-pointer max-w-[32px] max-h-[32px] min-w-[32px]"
        :src="variant.images"
        :variant="variant"
        :height="imageSize"
        :width="imageSize"
        :params="{ w: imageSize, h: imageSize, fit: 'contain' }"
        :alt="variant.name"
        @click="emit('open-image-modal')"
      />
      <div v-if="artNrAttribute" class="flex gap-2 items-center @4xl:ml-0 @4xl:mr-auto" :class="artNrClass.container">
        <CText el="span" variant="system-sm-bold" class="@4xl:hidden flex-1">
          {{ layout === 'plp' ? t('article_number') : t('art_nr') }}
        </CText>
        <div class="flex items-center flex-1 gap-2" :class="artNrClass.number">
          <VariantsAttribute
            v-if="selectedArticleNumber === variant.articleNumber"
            :value="getAttributeValue(artNrAttribute)"
            @click.prevent
          />
          <VariantLink
            v-else
            :variant="variant"
            :list="config.public.gtm.PRODUCT_VARIANTS_LIST"
            @click.stop="emit('track-navigation')"
          >
            <VariantsAttribute :value="getAttributeValue(artNrAttribute)" />
          </VariantLink>
          <div class="relative hidden @4xl:block">
            <CIcon
              name="copy_all"
              class="cursor-pointer hover:text-gray-400"
              tabindex="0"
              @click="
                (event) => {
                  copyToClipBoard(event, variant.articleNumber);
                  copyText = 'copied';
                }
              "
              @mouseenter="showCopyText = true"
              @mouseleave="
                copyText = 'copy_art_nr';
                showCopyText = false;
              "
              @keyup.enter="(event) => copyToClipBoard(event, variant.articleNumber)"
            />
            <div v-if="showCopyText" class="variant-table__copy-tooltip">
              {{ t(copyText) }}
            </div>
          </div>
        </div>
        <DiscontinuedLabel v-if="variant.discontinued" />
        <CIcon
          v-if="variant.discontinued"
          class="relative flex justify-center rounded-full cursor-pointer underline-none"
          name="info"
          @click.prevent.stop="emit('open-replacement-modal')"
        />
      </div>
    </td>
    <!-- Attribute columns repeated. -->
    <td
      v-for="(attribute, i) in variantAttributes"
      :key="i"
      class="flex h-8 @4xl:h-[46px] variant-table-cell repeated"
      :class="{
        selected: isSelected,
        'variant-name-attribute': attribute?.attributeName === 'name',
      }"
    >
      <CText variant="system-sm" el="span" class="@4xl:hidden">{{ tableHeaders[i].label }}</CText>
      <VariantsAttribute
        :value="attribute ? getAttributeValue(attribute) : '-'"
        :character-limit="
          brandConfig.additions.showVariantNameInTable && attribute?.attributeName === 'name' ? 64 : undefined
        "
      />
    </td>
    <!-- Empty column. Used for scroll behaviour in desktop. Not visible in mobile -->
    <td
      class="h-[0] @4xl:h-[46px] flex variant-table-cell empty"
      :class="{
        last: !isLoggedIn,
        selected: isSelected,
      }"
    ></td>
    <!-- Column for global stock. Only visible in mobile -->
    <td
      v-if="isLoggedIn && centralLocationId"
      class="@4xl:hidden h-8 flex variant-table-cell"
      :class="{
        selected: isSelected,
        'border-black border-y-0 border-x': isSelected,
      }"
      @click.prevent.stop
    >
      <CText variant="system-sm-bold">{{ t('central_stock') }}</CText>
      <div>
        <CLoader v-if="isLoading.stock" duration="0.5" />
        <StockIndicator
          v-else
          data-test="central-stock-quantity"
          v-bind="centralStock"
          @click.prevent.stop
          @display-infobox="emit('open-stock-modal', 'global_stock_status')"
        />
      </div>
    </td>
    <!-- Column for store stock. Only visible in mobile -->
    <td
      v-if="isLoggedIn"
      data-test="store-stock-quantity"
      class="@4xl:hidden h-8 flex variant-table-cell"
      :class="{
        selected: isSelected,
      }"
      @click.prevent.stop
    >
      <CText variant="system-sm-bold">{{ t('store_stock') }}</CText>
      <div>
        <CLoader v-if="isLoading.stock" duration="0.5" />
        <StockIndicator
          v-else
          v-bind="storeStock"
          @click.prevent.stop
          @display-infobox="emit('open-stock-modal', 'store_stock_status')"
        />
      </div>
    </td>
    <!-- Column for price. Only visible in mobile -->
    <td
      v-if="isLoggedIn"
      class="@4xl:hidden h-8 flex variant-table-cell"
      :class="{
        selected: isSelected,
      }"
      @click.prevent.stop
    >
      <CText variant="system-sm-bold">{{ t('price.price') }}</CText>
      <div>
        <CLoader v-if="isLoading.price" duration="0.5" />
        <PriceIndicator
          v-else
          v-bind="price"
          :full-width="true"
          @click.prevent.stop
          @display-infobox="emit('open-price-modal')"
        />
      </div>
    </td>
    <!-- Fixed area (right). Buy button (visible for all screen sizes). Price & stock only visible in desktop -->
    <td
      v-if="isLoggedIn"
      class="@4xl:sticky flex last right-0 h-14 @4xl:h-[46px] variant-table-cell"
      :class="{
        selected: isSelected,
        '@4xl:border-b-gray-200': !isScrolledToEnd && isOverflowing && !isSelected,
        '@4xl:shadow-[-5px_5px_8px_0_rgba(0,0,0,0.08)]': !isScrolledToEnd && isOverflowing,
      }"
      @click.prevent.stop
    >
      <div
        v-if="centralLocationId"
        class="box-content shrink-0 hidden @4xl:flex items-center !w-16 justify-end py-0 pr-1 pl-0"
      >
        <CLoader v-if="isLoading.stock" duration="0.5" />
        <StockIndicator
          v-else
          v-bind="centralStock"
          @click.prevent.stop
          @display-infobox="emit('open-stock-modal', 'global_stock_status')"
        />
      </div>
      <div class="box-content shrink-0 px-2 hidden @4xl:flex items-center !w-16 justify-end pt-0 pr-1 pb-0 pl-0">
        <CLoader v-if="isLoading.stock" duration="0.5" />
        <StockIndicator
          v-else
          v-bind="storeStock"
          @click.prevent.stop
          @display-infobox="emit('open-stock-modal', 'store_stock_status')"
        />
      </div>
      <div class="box-content hidden @4xl:flex items-center w-28 px-2 shrink-0 whitespace-nowrap">
        <CLoader v-if="isLoading.price" duration="0.5" />
        <PriceIndicator
          v-else
          v-bind="price"
          :full-width="true"
          @click.prevent.stop
          @display-infobox="emit('open-price-modal')"
        />
      </div>
      <div class="flex items-center justify-end w-full">
        <CLoader v-if="isLoading.stock" duration="0.5" />
        <AddToCart v-else-if="isBuyable" v-bind="addToCartProps" @click.prevent.stop />
      </div>
      <div v-if="renderReplacementLink">
        <CLink variant="primary" size="small" :to="`/produkt/${replacement!.variantSlug}`" @click.prevent.stop>
          {{ t('replacement_article') }}
        </CLink>
      </div>
    </td>
  </VariantLink>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import type { Variant, VariantTableHeader } from '@/types/products';
import { useUserStore, useDeliveryStore, useBrandStore } from '@/stores';
import type { StockLocation } from '@/types/stock';
import type { CButtonSize } from '@/types/ui';

type Props = {
  variant: Variant;
  tableHeaders: VariantTableHeader[];
  layout: 'pdp' | 'plp';
  isWideTable: boolean;
  commonVariantAttributes?: any[];
  selectedArticleNumber: string;
  isScrolled: boolean;
  isScrolledToEnd: boolean;
  isOverflowing: boolean;
  isAnyVariantDiscontinued: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  commonVariantAttributes: () => [],
});

const emit = defineEmits<{
  (e: 'open-image-modal'): void;
  (e: 'track-navigation'): void;
  (e: 'open-price-modal'): void;
  (e: 'open-replacement-modal'): void;
  (e: 'open-stock-modal', type: string): void;
}>();

const isLoading = inject('isLoading', ref({ stock: false, price: false }));

const { mq } = useMediaQueries();
const { getAttributeValue } = useProductAttributes();
const { t } = useI18n();
const config = useRuntimeConfig();
const { brandConfig } = useBrandStore();

const { isLoggedIn } = storeToRefs(useUserStore());
const { getCurrentStockLocation } = storeToRefs(useDeliveryStore());

const artNrColumnHeader: VariantTableHeader = {
  label: 'Art. nr.',
  name: 'articleNumber',
  sort: '0',
};

const copyText = ref('copy_art_nr');
const showCopyText = ref(false);

const imageSize = computed(() => (mq.value.xsmall || mq.value.small ? '24' : '32'));
const centralLocationId = computed(() => brandConfig.centralStorageId);
const isSelected = computed(() => props.variant.articleNumber === props.selectedArticleNumber);
const artNrAttribute = computed(() =>
  [...(props.variant?.attributes || []), ...props.commonVariantAttributes].find(
    (attribute) => attribute.attributeName === artNrColumnHeader.name,
  ),
);

const price = computed(() => ({
  price: props.variant.price?.netPrice,
  priceUnit: props.variant.webSalesUnit?.unit || props.variant.price?.salesPriceUnit,
  priceType: props.variant.price?.priceType,
  displayInfoIcon: true,
  askForPrice: props.variant.price?.askForPrice,
  salesUnits: props.variant.webSalesUnit,
}));

const addToCartProps = computed(() => ({
  variant: props.variant,
  commonVariantAttributes: props.commonVariantAttributes,
  isFavoritesIconVisible: true,
  size: 'medium' as CButtonSize,
  loading: isLoading.value.stock,
  layout: 'add-to-cart--variantTile',
}));

const isBuyable = computed(() => {
  if (props.variant.discontinued && props.variant.stock?.locations?.length) {
    return props.variant.stock.locations.some((location) => location.quantity > 0);
  }
  return true;
});

const variantAttributes = computed(() => {
  const attributes = props.variant.attributes || [];
  const row = props.tableHeaders.map((header) => {
    return attributes?.find((attribute) => attribute.attributeName === header.name);
  });
  return row;
});

const getLocation = (locationId?: string) => {
  return props.variant?.stock?.locations?.find((location) => location.locationId === locationId);
};

const trackNavigation = () => {
  if (props.selectedArticleNumber !== props.variant.articleNumber) return;
  emit('track-navigation');
};

const getStock = (location?: StockLocation) => {
  if (!props.variant.stock?.locations) {
    return { quantity: -1, inStockText: '-', isTextVisible: false };
  }

  if (!location?.locationId) {
    return {
      quantity: 0,
      displayInfoIcon: true,
      isTextVisible: false,
    };
  }

  const [transformedLocation] = transformStock({
    stock: { ...props.variant.stock, locations: [location] },
    centralStorageId: brandConfig.centralStorageId,
    brandConfig,
  });
  return {
    ...transformedLocation,
    unit: '',
    inStockText: '',
    isTextVisible: false,
    isPurchasable: isPurchasableOnline(props.variant, getCurrentStockLocation.value),
    outOfStockText: '',
    subAvailabilityDates: [],
    availabilityDate: undefined,
  };
};

const centralStock = computed(() => {
  const location = getLocation(centralLocationId.value);
  const transformedLocation = getStock(location);
  return {
    ...transformedLocation,
    displayInfoIcon: (location?.quantity ? Math.max(location.quantity, 0) : 0) === 0,
  };
});

const storeStock = computed(() => {
  const location = getLocation(getCurrentStockLocation.value);
  const transformedLocation = getStock(location);
  return {
    ...transformedLocation,
    displayInfoIcon: true,
  };
});

const replacement = computed(() => props.variant.relations?.ersattningsartikel?.articles?.[0]);

const renderReplacementLink = computed(() => {
  return (
    !isBuyable.value &&
    !isLoading.value.price &&
    !isLoading.value.stock &&
    props.variant.discontinued &&
    replacement.value
  );
});

const artNrClass = computed(() => {
  return {
    container: props.layout === 'plp' ? 'justify-between w-full' : '',
    number: [props.layout === 'plp' ? 'justify-end' : 'justify-between', mq.value.xsmall ? 'font-bold' : '']
      .filter(Boolean)
      .join(' '),
  };
});
</script>

<script lang="ts">
export default defineComponent({
  name: 'VariantTableRow',
});
</script>

<style lang="scss" scoped>
@container (max-width: 56rem) {
  .variant-name-attribute {
    @apply h-16;
  }
  :deep(.variant-name-attribute .attr) {
    @apply break-normal whitespace-normal text-right;
  }
}

.variant-table {
  &-cell {
    @apply bg-white px-2 gap-4 justify-between items-center text-sm;
    @apply border-b border-solid;
    // Desktop
    @apply @4xl:text-xs;

    // For each repeated col (not fixed area like art.nr, buy button, and empty area)
    &.repeated {
      // Mobile
      @apply pointer-events-none;
      // Desktop
      @apply @4xl:pointer-events-auto;
    }

    &.selected {
      // Mobile
      @apply border-solid border-x cursor-auto border-black border-b-gray-200;
      // Desktop
      @apply @4xl:bg-gray-100 @4xl:border-b-black @4xl:border-y;

      &:not(:first-child) {
        @apply border-t-0  @4xl:border-t;
      }
      &.repeated {
        @apply @4xl:border-x-0;
      }
      &:not(.last) {
        @apply @4xl:border-r-0;
      }
      &.last {
        @apply @4xl:border border-b-black;
      }
      &.last,
      &.empty {
        @apply @4xl:border-l-0 @4xl:border-t;
      }
      &.empty {
        @apply @4xl:border-t;
      }

      &:first-child {
        @apply border-t;
        @apply @4xl:border-l @4xl:border-r-0;
      }
    }
    &:not(.selected) {
      // Mobile
      @apply cursor-auto border-b-gray-200;
      // Desktop
      @apply @4xl:group-hover:bg-gray-100 @4xl:cursor-pointer;
      @apply @4xl:border-b-transparent;

      &.last {
        @apply border-b-transparent;
      }
    }
    &.empty:not(.last) {
      @apply border-b-0 @4xl:border-b;
    }
    &.empty.last {
      @apply hidden @4xl:flex;
    }

    &.last {
      @apply mb-4 @4xl:mb-0;
    }
  }

  &__copy-tooltip {
    @apply absolute p-3 text-xs bg-white shadow-lg whitespace-nowrap top-4 left-4;
  }

  &-row:last-of-type &__copy-tooltip {
    @apply -top-2;
  }
}
</style>
